/**
 * Utility functions to detect file types from URLs
 */

import { 
  faFilePdf,
  faFileAlt,
  faImage,
  faVideo,
  faMusic,
  faTable,
  faFilePowerpoint,
  faFileArchive,
  faCode,
  faFile
} from '@fortawesome/free-solid-svg-icons';

export const getFileTypeFromUrl = (url) => {
  if (!url) return null;
  
  // Remove query parameters and fragments for cleaner extension detection
  const cleanUrl = url.split('?')[0].split('#')[0];
  
  // Get file extension
  const extension = cleanUrl.split('.').pop()?.toLowerCase();
  
  const fileTypes = {
    // Documents
    'pdf': 'pdf',
    'doc': 'document',
    'docx': 'document',
    'txt': 'document',
    'rtf': 'document',
    'odt': 'document',
    
    // Images
    'jpg': 'image',
    'jpeg': 'image',
    'png': 'image',
    'gif': 'image',
    'svg': 'image',
    'webp': 'image',
    'bmp': 'image',
    'tiff': 'image',
    'ico': 'image',
    
    // Videos
    'mp4': 'video',
    'avi': 'video',
    'mov': 'video',
    'wmv': 'video',
    'webm': 'video',
    'mkv': 'video',
    'flv': 'video',
    'm4v': 'video',
    
    // Audio
    'mp3': 'audio',
    'wav': 'audio',
    'aac': 'audio',
    'flac': 'audio',
    'ogg': 'audio',
    'm4a': 'audio',
    
    // Spreadsheets
    'xls': 'spreadsheet',
    'xlsx': 'spreadsheet',
    'csv': 'spreadsheet',
    'ods': 'spreadsheet',
    
    // Presentations
    'ppt': 'presentation',
    'pptx': 'presentation',
    'odp': 'presentation',
    
    // Archives
    'zip': 'archive',
    'rar': 'archive',
    '7z': 'archive',
    'tar': 'archive',
    'gz': 'archive',
    
    // Code
    'js': 'code',
    'html': 'code',
    'css': 'code',
    'json': 'code',
    'xml': 'code',
    'py': 'code',
    'java': 'code',
    'cpp': 'code',
    'c': 'code'
  };
  
  return fileTypes[extension] || 'unknown';
};

export const getFileTypeFromService = (url) => {
  if (!url) return null;
  
  // Google Drive patterns
  if (url.includes('drive.google.com') || url.includes('docs.google.com')) {
    if (url.includes('/document/')) return 'document';
    if (url.includes('/spreadsheets/')) return 'spreadsheet';
    if (url.includes('/presentation/')) return 'presentation';
    if (url.includes('/drawings/')) return 'image';
    if (url.includes('/forms/')) return 'document';
    if (url.includes('/file/d/')) {
      // Generic Google Drive file, try to get type from export format or fallback to extension
      if (url.includes('export=pdf')) return 'pdf';
      if (url.includes('export=xlsx')) return 'spreadsheet';
      if (url.includes('export=pptx')) return 'presentation';
      if (url.includes('export=docx')) return 'document';
      
      // For generic /file/d/ URLs, we'll need to use advanced detection
      // Mark this for header-based detection
      return 'google-drive-file';
    }
  }
  
  // Dropbox patterns
  if (url.includes('dropbox.com')) {
    // Dropbox shared links often don't have extensions in the URL
    if (url.includes('?dl=0') || url.includes('?dl=1')) {
      // Try to extract filename from URL path before the query parameters
      const pathPart = url.split('?')[0];
      const detectedType = getFileTypeFromUrl(pathPart);
      if (detectedType !== 'unknown') return detectedType;
    }
    return getFileTypeFromUrl(url);
  }
  
  // OneDrive patterns
  if (url.includes('onedrive.live.com') || url.includes('sharepoint.com') || url.includes('1drv.ms')) {
    // OneDrive shared links
    if (url.includes('Word.Document') || url.includes('/Document.aspx')) return 'document';
    if (url.includes('Excel.Sheet') || url.includes('/Workbook.aspx')) return 'spreadsheet';
    if (url.includes('PowerPoint.Presentation') || url.includes('/Presentation.aspx')) return 'presentation';
    if (url.includes('/Photo.aspx') || url.includes('/Picture.aspx')) return 'image';
    return getFileTypeFromUrl(url);
  }
  
  // Box patterns
  if (url.includes('box.com')) {
    if (url.includes('/s/')) {
      // Box shared links, try to get from path
      return getFileTypeFromUrl(url);
    }
  }
  
  // Adobe Creative Cloud
  if (url.includes('adobe.com')) {
    if (url.includes('acrobat.com') || url.includes('documentcloud')) return 'pdf';
    if (url.includes('creative.adobe.com')) {
      if (url.includes('/express/')) return 'image';
    }
  }
  
  // Figma
  if (url.includes('figma.com')) {
    if (url.includes('/file/')) return 'image';
    if (url.includes('/proto/')) return 'presentation';
  }
  
  // Canva
  if (url.includes('canva.com')) {
    if (url.includes('/design/')) return 'image';
  }
  
  // Notion
  if (url.includes('notion.so') || url.includes('notion.site')) {
    return 'document';
  }
  
  // Airtable
  if (url.includes('airtable.com')) {
    if (url.includes('/tbl')) return 'spreadsheet';
  }
  
  // YouTube
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return 'video';
  }
  
  // Vimeo
  if (url.includes('vimeo.com')) {
    return 'video';
  }
  
  // SoundCloud
  if (url.includes('soundcloud.com')) {
    return 'audio';
  }
  
  // Spotify
  if (url.includes('spotify.com')) {
    return 'audio';
  }
  
  // GitHub patterns
  if (url.includes('github.com')) {
    if (url.includes('/blob/')) return getFileTypeFromUrl(url);
    if (url.includes('/raw/')) return getFileTypeFromUrl(url);
    if (url.includes('/releases/')) return getFileTypeFromUrl(url);
  }
  
  // GitLab patterns
  if (url.includes('gitlab.com')) {
    if (url.includes('/-/blob/') || url.includes('/-/raw/')) return getFileTypeFromUrl(url);
  }
  
  // Bitbucket patterns
  if (url.includes('bitbucket.org')) {
    if (url.includes('/src/') || url.includes('/raw/')) return getFileTypeFromUrl(url);
  }
  
  // AWS S3 patterns
  if (url.includes('amazonaws.com') || url.includes('s3.')) {
    return getFileTypeFromUrl(url);
  }
  
  // Generic cloud storage patterns
  if (url.includes('cdn.') || url.includes('storage.') || url.includes('blob.')) {
    return getFileTypeFromUrl(url);
  }
  
  // Fallback to extension method
  return getFileTypeFromUrl(url);
};

export const getFileTypeIcon = (fileType) => {
  const iconMap = {
    'pdf': faFilePdf,
    'document': faFileAlt,
    'image': faImage,
    'video': faVideo,
    'audio': faMusic,
    'spreadsheet': faTable,
    'presentation': faFilePowerpoint,
    'archive': faFileArchive,
    'code': faCode,
    'unknown': faFile
  };
  
  return iconMap[fileType] || iconMap.unknown;
};

export const getFileTypeLabel = (fileType) => {
  const labelMap = {
    'pdf': 'PDF Document',
    'document': 'Document',
    'image': 'Image',
    'video': 'Video',
    'audio': 'Audio',
    'spreadsheet': 'Spreadsheet',
    'presentation': 'Presentation',
    'archive': 'Archive',
    'code': 'Code',
    'unknown': 'File'
  };
  
  return labelMap[fileType] || labelMap.unknown;
};

export const getFileTypeColor = (fileType) => {
  const colorMap = {
    'pdf': '#8A42EC',        // Brand Purple (PDFs)
    'document': '#5B21B6',   // Deep Purple (Documents)
    'image': '#059669',      // Professional Green (Images)
    'video': '#7C2D92',      // Dark Purple (Videos)
    'audio': '#6366F1',      // Indigo (Audio)
    'spreadsheet': '#16A34A', // Professional Green (Spreadsheets)
    'presentation': '#8A42EC', // Brand Purple (Presentations)
    'archive': '#6B7280',    // Professional Gray (Archives)
    'code': '#374151',       // Dark Gray (Code)
    'unknown': '#6B7280'     // Professional Gray (Unknown)
  };
  
  return colorMap[fileType] || colorMap.unknown;
};

// Main function that combines all detection methods
export const detectFileType = (url) => {
  // First try service-specific detection
  const serviceType = getFileTypeFromService(url);
  
  // Handle Google Drive files - return a reasonable default since we can't do async
  if (serviceType === 'google-drive-file') {
    // We can't do async detection here, so return a generic 'unknown' 
    // but the caller can use detectFileTypeAdvanced() for better results
    return 'unknown';
  }
  
  if (serviceType && serviceType !== 'unknown') {
    return serviceType;
  }
  
  // Fallback to extension detection
  return getFileTypeFromUrl(url);
};

// Helper to get all file info at once
export const getFileInfo = (url) => {
  const type = detectFileType(url);
  return {
    type,
    icon: getFileTypeIcon(type),
    label: getFileTypeLabel(type),
    color: getFileTypeColor(type),
    // Flag to indicate if advanced detection might yield better results
    needsAdvancedDetection: type === 'unknown' && (
      url.includes('drive.google.com/file/d/') ||
      url.includes('dropbox.com') ||
      url.includes('onedrive.') ||
      url.includes('box.com')
    )
  };
};

// Advanced detection using HTTP HEAD request (optional, for when URL patterns fail)
export const getFileTypeFromHeaders = async (url) => {
  try {
    const response = await fetch(url, { 
      method: 'HEAD',
      mode: 'cors',
      headers: {
        'Accept': '*/*'
      }
    });
    
    const contentType = response.headers.get('content-type')?.toLowerCase();
    if (!contentType) return null;
    
    const mimeToTypeMap = {
      // Documents
      'application/pdf': 'pdf',
      'application/msword': 'document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
      'text/plain': 'document',
      'text/rtf': 'document',
      'application/rtf': 'document',
      
      // Images
      'image/jpeg': 'image',
      'image/jpg': 'image',
      'image/png': 'image',
      'image/gif': 'image',
      'image/svg+xml': 'image',
      'image/webp': 'image',
      'image/bmp': 'image',
      'image/tiff': 'image',
      
      // Videos
      'video/mp4': 'video',
      'video/avi': 'video',
      'video/quicktime': 'video',
      'video/x-msvideo': 'video',
      'video/webm': 'video',
      'video/x-matroska': 'video',
      
      // Audio
      'audio/mpeg': 'audio',
      'audio/mp3': 'audio',
      'audio/wav': 'audio',
      'audio/aac': 'audio',
      'audio/flac': 'audio',
      'audio/ogg': 'audio',
      
      // Spreadsheets
      'application/vnd.ms-excel': 'spreadsheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'spreadsheet',
      'text/csv': 'spreadsheet',
      
      // Presentations
      'application/vnd.ms-powerpoint': 'presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'presentation',
      
      // Archives
      'application/zip': 'archive',
      'application/x-rar-compressed': 'archive',
      'application/x-7z-compressed': 'archive',
      'application/x-tar': 'archive',
      'application/gzip': 'archive',
      
      // Code/Text
      'application/json': 'code',
      'application/xml': 'code',
      'text/html': 'code',
      'text/css': 'code',
      'text/javascript': 'code',
      'application/javascript': 'code'
    };
    
    // Find exact match first
    if (mimeToTypeMap[contentType]) {
      return mimeToTypeMap[contentType];
    }
    
    // Try partial matches for complex MIME types
    for (const [mime, type] of Object.entries(mimeToTypeMap)) {
      if (contentType.includes(mime)) {
        return type;
      }
    }
    
    // Try general category matches
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.startsWith('video/')) return 'video';
    if (contentType.startsWith('audio/')) return 'audio';
    if (contentType.startsWith('text/')) return 'document';
    
    return null;
  } catch (error) {
    // CORS or network issues - this is expected for many URLs
    console.debug('Could not fetch headers for URL:', url, error.message);
    return null;
  }
};

// Enhanced detection that tries multiple methods
export const detectFileTypeAdvanced = async (url) => {
  // First try service-specific detection (fastest)
  const serviceType = getFileTypeFromService(url);
  
  // Handle Google Drive files specifically
  if (serviceType === 'google-drive-file') {
    const driveType = await detectGoogleDriveFileType(url);
    if (driveType) {
      return driveType;
    }
  } else if (serviceType && serviceType !== 'unknown') {
    return serviceType;
  }
  
  // Try extension detection
  const extensionType = getFileTypeFromUrl(url);
  if (extensionType && extensionType !== 'unknown') {
    return extensionType;
  }
  
  // As last resort, try HTTP headers (may fail due to CORS)
  try {
    const headerType = await getFileTypeFromHeaders(url);
    if (headerType) {
      return headerType;
    }
  } catch (error) {
    // Silent fail - CORS errors are expected
  }
  
  return 'unknown';
};

// Google Drive specific detection methods
export const extractGoogleDriveFileId = (url) => {
  // Extract file ID from various Google Drive URL formats
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9-_]+)/,  // /file/d/{fileId}
    /id=([a-zA-Z0-9-_]+)/,          // ?id={fileId}
    /\/d\/([a-zA-Z0-9-_]+)/,        // /d/{fileId}
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

export const getGoogleDriveDirectUrl = (fileId) => {
  // Convert to direct download URL for header checking
  return `https://drive.google.com/uc?export=download&id=${fileId}`;
};

export const getGoogleDriveThumbnailUrl = (fileId) => {
  // Google Drive thumbnail URL (can sometimes indicate file type)
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=s220`;
};

export const detectGoogleDriveFileType = async (url) => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) return null;
  
  try {
    // Method 1: Try direct download URL with HEAD request
    const directUrl = getGoogleDriveDirectUrl(fileId);
    const headerType = await getFileTypeFromHeaders(directUrl);
    if (headerType && headerType !== 'unknown') {
      return headerType;
    }
    
    // Method 2: Try thumbnail URL (images will return 200, others may redirect/error)
    const thumbnailUrl = getGoogleDriveThumbnailUrl(fileId);
    try {
      const thumbnailResponse = await fetch(thumbnailUrl, { 
        method: 'HEAD',
        mode: 'cors'
      });
      
      if (thumbnailResponse.ok) {
        const contentType = thumbnailResponse.headers.get('content-type');
        if (contentType && contentType.startsWith('image/')) {
          // If thumbnail returns an image, the original might be an image
          // But this isn't 100% reliable as Google generates thumbnails for many file types
          return 'image';
        }
      }
    } catch (e) {
      // Thumbnail method failed, continue
    }
    
    // Method 3: Try to access the file with different export formats to infer type
    const exportTests = [
      { format: 'pdf', type: 'document' },
      { format: 'xlsx', type: 'spreadsheet' },
      { format: 'pptx', type: 'presentation' }
    ];
    
    for (const test of exportTests) {
      try {
        const exportUrl = `https://docs.google.com/document/d/${fileId}/export?format=${test.format}`;
        const response = await fetch(exportUrl, { method: 'HEAD', mode: 'cors' });
        if (response.ok) {
          return test.type;
        }
      } catch (e) {
        // Export failed, try next
      }
    }
    
  } catch (error) {
    console.debug('Google Drive detection failed:', error);
  }
  
  return null;
};

// Detect file type from task name/title using keyword matching
export const getFileTypeFromTaskName = (taskName) => {
  if (!taskName) return null;
  
  const title = taskName.toLowerCase();
  
  // Define keyword patterns for each file type
  const patterns = {
    'image': [
      'image', 'graphic', 'graphics', 'design', 'logo', 'banner', 'icon', 
      'photo', 'picture', 'visual', 'artwork', 'creative', 'illustration',
      'mockup', 'thumbnail', 'header', 'social media post', 'infographic'
    ],
    'video': [
      'video', 'explainer', 'animation', 'commercial', 'promo', 'trailer',
      'tutorial', 'demo', 'recording', 'episode', 'webinar', 'training video',
      'product demo', 'walkthrough', 'screen recording'
    ],
    'pdf': [
      'pdf', 'document', 'guide', 'manual', 'report', 'whitepaper', 
      'ebook', 'brochure', 'flyer', 'proposal', 'contract', 'agreement',
      'policy', 'handbook', 'checklist'
    ],
    'presentation': [
      'presentation', 'slide', 'deck', 'powerpoint', 'slides', 'pitch',
      'keynote', 'training deck', 'sales deck', 'overview', 'slideshow'
    ],
    'spreadsheet': [
      'spreadsheet', 'excel', 'data', 'analysis', 'report', 'tracking',
      'dashboard', 'metrics', 'calculator', 'budget', 'forecast'
    ],
    'document': [
      'document', 'plan', 'strategy', 'outline', 'brief', 'copy', 'content',
      'script', 'guidelines', 'requirements', 'specification', 'template'
    ]
  };
  
  // Check each pattern category
  for (const [fileType, keywords] of Object.entries(patterns)) {
    for (const keyword of keywords) {
      if (title.includes(keyword)) {
        return fileType;
      }
    }
  }
  
  return null;
};

// Enhanced detection that includes task name analysis
export const detectFileTypeEnhanced = (url, taskName = null) => {
  // First try URL-based detection (most reliable)
  const urlType = getFileTypeFromService(url);
  if (urlType && urlType !== 'unknown' && urlType !== 'google-drive-file') {
    return urlType;
  }
  
  // Try extension detection
  const extensionType = getFileTypeFromUrl(url);
  if (extensionType && extensionType !== 'unknown') {
    return extensionType;
  }
  
  // Try task name analysis as fallback
  if (taskName) {
    const nameType = getFileTypeFromTaskName(taskName);
    if (nameType) {
      return nameType;
    }
  }
  
  // Mark for advanced detection if it's a Google Drive file
  if (urlType === 'google-drive-file') {
    return 'google-drive-file';
  }
  
  return 'unknown';
};

// Helper to get enhanced file info with task name
export const getEnhancedFileInfo = (url, taskName = null) => {
  const type = detectFileTypeEnhanced(url, taskName);
  return {
    type,
    icon: getFileTypeIcon(type),
    label: getFileTypeLabel(type),
    color: getFileTypeColor(type),
    // Flag to indicate if advanced detection might yield better results
    needsAdvancedDetection: type === 'unknown' || type === 'google-drive-file'
  };
}; 